import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Trophy, Users, Clock, Target, Star, Award,
  CheckCircle, Code, ExternalLink, Loader, Download, Mail,
  Zap, Calendar, UserCheck, XCircle, Phone, Briefcase, MoreVertical
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CompanyChallengeDetailPage = () => {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const token = localStorage.getItem('company_token');

  useEffect(() => {
    if (!token) {
      navigate('/company/auth');
      return;
    }
    fetchChallenge();
  }, [challengeId, token, navigate]);

  const fetchChallenge = async () => {
    try {
      const res = await axios.get(`${API}/api/company/hiring-challenges/${challengeId}/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChallenge(res.data.challenge);
      setLeaderboard(res.data.leaderboard || []);
    } catch (err) {
      toast.error('Failed to load challenge');
      navigate('/company/hiring-challenges');
    } finally {
      setLoading(false);
    }
  };

  const updateCandidateStatus = async (participantId, status, participantEmail) => {
    setUpdatingStatus(participantId);
    try {
      await axios.post(
        `${API}/api/company/hiring-challenges/${challengeId}/update-status`,
        { participant_id: participantId, status, email: participantEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Candidate ${status === 'shortlisted' ? 'shortlisted' : status === 'rejected' ? 'rejected' : status === 'interview' ? 'scheduled for interview' : 'marked as hired'}!`);
      setActionMenuOpen(null);
      fetchChallenge();
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleShortlist = async () => {
    if (selectedParticipants.length === 0) {
      toast.error('Select participants to shortlist');
      return;
    }

    try {
      await axios.post(
        `${API}/api/company/hiring-challenges/${challengeId}/shortlist`,
        selectedParticipants,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Shortlisted ${selectedParticipants.length} participants`);
      setSelectedParticipants([]);
      fetchChallenge();
    } catch (err) {
      toast.error('Failed to shortlist');
    }
  };

  const toggleSelect = (participantId) => {
    setSelectedParticipants(prev => 
      prev.includes(participantId)
        ? prev.filter(id => id !== participantId)
        : [...prev, participantId]
    );
  };

  const selectAll = () => {
    if (selectedParticipants.length === leaderboard.length) {
      setSelectedParticipants([]);
    } else {
      setSelectedParticipants(leaderboard.map(p => p.id));
    }
  };

  const getRankBadge = (rank) => {
    if (rank === 1) return <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center text-white font-bold">1</div>;
    if (rank === 2) return <div className="w-8 h-8 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full flex items-center justify-center text-white font-bold">2</div>;
    if (rank === 3) return <div className="w-8 h-8 bg-gradient-to-br from-amber-600 to-orange-700 rounded-full flex items-center justify-center text-white font-bold">3</div>;
    return <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-medium">{rank}</div>;
  };

  const getStatusBadge = (status) => {
    const badges = {
      shortlisted: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Shortlisted' },
      interview: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Interview' },
      hired: { bg: 'bg-green-100', text: 'text-green-700', label: 'Hired' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' }
    };
    const badge = badges[status];
    if (!badge) return null;
    return (
      <span className={`px-2 py-0.5 ${badge.bg} ${badge.text} text-xs rounded-full font-medium`}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader className="animate-spin text-orange-500" size={32} />
      </div>
    );
  }

  if (!challenge) return null;

  const getStatus = () => {
    const now = new Date().toISOString();
    if (challenge.end_time && now > challenge.end_time) return 'ended';
    if (challenge.start_time && now >= challenge.start_time) return 'live';
    return 'upcoming';
  };

  const status = getStatus();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/company/hiring-challenges" className="text-gray-400 hover:text-gray-600">
                <ArrowLeft size={20} />
              </Link>
              <div>
                <h1 className="font-bold text-gray-900">{challenge.title}</h1>
                <p className="text-xs text-gray-500">{leaderboard.length} participants</p>
              </div>
            </div>
            
            {selectedParticipants.length > 0 && (
              <button
                onClick={handleShortlist}
                className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
              >
                <UserCheck size={18} />
                Shortlist ({selectedParticipants.length})
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Challenge Info Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex flex-wrap items-center gap-4 mb-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              status === 'live' ? 'bg-green-100 text-green-700' :
              status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
              'bg-gray-100 text-gray-600'
            }`}>
              {status === 'live' ? '🔴 Live' : status === 'upcoming' ? '📅 Upcoming' : 'Ended'}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm border ${
              challenge.difficulty === 'easy' ? 'bg-green-50 text-green-700 border-green-200' :
              challenge.difficulty === 'medium' ? 'bg-amber-50 text-amber-700 border-amber-200' :
              'bg-red-50 text-red-700 border-red-200'
            }`}>
              {challenge.difficulty}
            </span>
            {challenge.is_public && (
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                Public
              </span>
            )}
          </div>
          
          <p className="text-gray-600 mb-4">{challenge.description}</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <Clock className="mx-auto text-gray-400 mb-1" size={20} />
              <p className="text-lg font-bold text-gray-900">{challenge.duration_minutes}</p>
              <p className="text-xs text-gray-500">Minutes</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <Users className="mx-auto text-gray-400 mb-1" size={20} />
              <p className="text-lg font-bold text-gray-900">{challenge.participants_count || 0}</p>
              <p className="text-xs text-gray-500">Participants</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <Code className="mx-auto text-gray-400 mb-1" size={20} />
              <p className="text-lg font-bold text-gray-900">{challenge.problems?.length || 0}</p>
              <p className="text-xs text-gray-500">Problems</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <Target className="mx-auto text-gray-400 mb-1" size={20} />
              <p className="text-lg font-bold text-gray-900">{challenge.submissions_count || 0}</p>
              <p className="text-xs text-gray-500">Submissions</p>
            </div>
          </div>
          
          {challenge.skills_tested?.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {challenge.skills_tested.map((skill, idx) => (
                <span key={idx} className="px-3 py-1 bg-orange-50 text-orange-700 rounded-lg text-sm">
                  {skill}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Leaderboard */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="font-bold text-gray-900 flex items-center gap-2">
              <Trophy className="text-amber-500" size={20} />
              Leaderboard
            </h2>
            {leaderboard.length > 0 && (
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedParticipants.length === leaderboard.length}
                  onChange={selectAll}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                Select All
              </label>
            )}
          </div>
          
          {leaderboard.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {leaderboard.map((participant, idx) => (
                <div 
                  key={participant.id}
                  className={`px-6 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors ${
                    participant.is_shortlisted ? 'bg-orange-50/50' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedParticipants.includes(participant.id)}
                    onChange={() => toggleSelect(participant.id)}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  
                  {getRankBadge(idx + 1)}
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {/* Clickable name - links to EvalentX Arena profile */}
                      {participant.student_email ? (
                        <Link
                          to={`/codearena/profile/${participant.student_email.split('@')[0]}`}
                          className="font-semibold text-gray-900 hover:text-blue-600 hover:underline cursor-pointer"
                          title="View EvalentX Arena Profile"
                        >
                          {participant.student_name || 'Anonymous'}
                        </Link>
                      ) : (
                        <h3 className="font-semibold text-gray-900">
                          {participant.student_name || 'Anonymous'}
                        </h3>
                      )}
                      {getStatusBadge(participant.status)}
                    </div>
                    <p className="text-sm text-gray-500">{participant.student_college || participant.student_email}</p>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">{participant.score}</p>
                    <p className="text-xs text-gray-500">
                      {participant.problems_solved}/{participant.total_problems || 1} solved
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    {participant.student_email && (
                      <a
                        href={`mailto:${participant.student_email}`}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Send Email"
                      >
                        <Mail size={18} />
                      </a>
                    )}
                    
                    {/* Status Action Menu */}
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActionMenuOpen(actionMenuOpen === participant.id ? null : participant.id);
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                        title="Update Status"
                      >
                        <MoreVertical size={18} />
                      </button>
                      
                      {actionMenuOpen === participant.id && (
                        <div className="absolute right-0 top-10 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                          <button
                            onClick={() => updateCandidateStatus(participant.id, 'shortlisted', participant.student_email)}
                            disabled={updatingStatus === participant.id}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-orange-50 text-orange-700 flex items-center gap-2"
                          >
                            <Star size={14} /> Shortlist
                          </button>
                          <button
                            onClick={() => updateCandidateStatus(participant.id, 'interview', participant.student_email)}
                            disabled={updatingStatus === participant.id}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-blue-50 text-blue-700 flex items-center gap-2"
                          >
                            <Phone size={14} /> Schedule Interview
                          </button>
                          <button
                            onClick={() => updateCandidateStatus(participant.id, 'hired', participant.student_email)}
                            disabled={updatingStatus === participant.id}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-green-50 text-green-700 flex items-center gap-2"
                          >
                            <Briefcase size={14} /> Mark as Hired
                          </button>
                          <button
                            onClick={() => updateCandidateStatus(participant.id, 'rejected', participant.student_email)}
                            disabled={updatingStatus === participant.id}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-700 flex items-center gap-2"
                          >
                            <XCircle size={14} /> Reject
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Users className="mx-auto text-gray-300 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Participants Yet</h3>
              <p className="text-gray-500">
                {status === 'upcoming' 
                  ? 'Participants will appear here after the challenge starts'
                  : 'No one has participated in this challenge yet'}
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default CompanyChallengeDetailPage;
